import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, MapPin, User, ArrowLeft, Share2, Tag } from 'lucide-react';
import { getPressReleaseBySlug } from '@/lib/api';
import SEO from '@/components/SEO';

const PressReleaseDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [pressRelease, setPressRelease] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPressRelease = async () => {
      try {
        const response = await getPressReleaseBySlug(slug);
        setPressRelease(response.data);
      } catch (error) {
        console.error('Error fetching press release:', error);
        setError('Press release not found');
      } finally {
        setLoading(false);
      }
    };
    fetchPressRelease();
  }, [slug]);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: pressRelease.title,
        text: pressRelease.excerpt,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading press release...</p>
        </div>
      </div>
    );
  }

  if (error || !pressRelease) {
    return (
      <div className="min-h-screen bg-white">
        <div className="bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-4">Press Release Not Found</h2>
            <p className="text-gray-300 mb-8">The press release you're looking for doesn't exist or has been removed.</p>
            <Link
              to="/newsroom"
              className="inline-flex items-center space-x-2 px-6 py-3 bg-coral-600 text-white rounded-lg hover:bg-coral-700 transition-colors"
            >
              <ArrowLeft size={20} />
              <span>Back to Newsroom</span>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={pressRelease.title}
        description={pressRelease.excerpt || pressRelease.title}
        path={`/newsroom/${pressRelease.slug}`}
        type="article"
        article={{
          title: pressRelease.title,
          excerpt: pressRelease.excerpt,
          date: pressRelease.date,
          updatedAt: pressRelease.updatedAt,
          author: pressRelease.author,
          featuredImage: pressRelease.featuredImage
        }}
      />
      {/* Header */}
      <div className="bg-navy-900 text-white py-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate(-1)}
            data-testid="back-button"
            className="flex items-center space-x-2 text-gray-300 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to Newsroom</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
          {/* Featured Image */}
          {pressRelease.featuredImage && (
            <div className="aspect-video w-full overflow-hidden bg-gray-100">
              <img
                src={pressRelease.featuredImage}
                alt={pressRelease.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="p-8 sm:p-12">
            {/* Categories */}
            {pressRelease.categories.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {pressRelease.categories.map((cat) => (
                  <span
                    key={cat}
                    className="inline-block px-3 py-1 text-xs font-medium bg-navy-100 text-navy-700 rounded-full"
                  >
                    {cat}
                  </span>
                ))}
              </div>
            )}

            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6" data-testid="pr-title">
              {pressRelease.title}
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap gap-6 text-sm text-gray-600 mb-8 pb-8 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <User size={16} className="text-navy-600" />
                <span>{pressRelease.author}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Calendar size={16} className="text-navy-600" />
                <span>{new Date(pressRelease.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
              </div>
              <div className="flex items-center space-x-2">
                <MapPin size={16} className="text-navy-600" />
                <span>{pressRelease.location}</span>
              </div>
              <button
                onClick={handleShare}
                data-testid="share-button"
                className="flex items-center space-x-2 text-navy-600 hover:text-navy-700 font-medium"
              >
                <Share2 size={16} />
                <span>Share</span>
              </button>
            </div>

            {/* Content */}
            <div
              data-testid="pr-content"
              className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-a:text-navy-600 prose-a:no-underline hover:prose-a:underline"
              dangerouslySetInnerHTML={{ __html: pressRelease.content }}
            />

            {/* Tags */}
            {pressRelease.tags.length > 0 && (
              <div className="mt-8 pt-8 border-t border-gray-200">
                <div className="flex items-start space-x-3">
                  <Tag size={20} className="text-navy-600 mt-1 flex-shrink-0" />
                  <div className="flex flex-wrap gap-2">
                    {pressRelease.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-full hover:bg-navy-50 transition-colors"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Contact CTA */}
        <div className="mt-12 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 rounded-2xl p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-4">Need Help With Your Press Release?</h3>
          <p className="text-gray-300 mb-6">Our team can help you craft and distribute your news to the right audience.</p>
          <Link
            to="/contact"
            data-testid="contact-cta-button"
            className="inline-flex items-center space-x-2 px-6 py-3 bg-coral-600 text-white rounded-lg hover:bg-coral-700 transition-colors font-semibold"
          >
            <span>Contact Our Team</span>
          </Link>
        </div>
      </article>
    </div>
  );
};

export default PressReleaseDetail;
